import * as React from 'react';

import { cn } from '@mint-lib/tw-utils';
import { cva, type VariantProps } from 'class-variance-authority';

import { Input } from './input.jsx';
import { typographyVariants } from './typography.jsx';
const inputGroupVariants = cva(
  'group/input-group inline-flex w-full items-center justify-center overflow-hidden rounded-sm bg-ui-field02 shadow-none placeholder:text-text-placeholder',

  {
    variants: {
      size: {
        medium: 'h-10',
        small: 'h-8',
      },
      color: {
        default: [
          'border border-border-primary hover:border-action-focusBorder',
          //Focus state.
          'has-[>[data-slot=input-group-control]:focus-visible]:outline-2 has-[>[data-slot=input-group-control]:focus-visible]:-outline-offset-2 has-[>[data-slot=input-group-control]:focus-visible]:outline-action-focusBorder',
        ],
        warning:
          'border-2 border-warning-main focus-visible:border-warning-main focus-visible:outline-none',
        error:
          'border-2 border-error-main focus-visible:border-error-main focus-visible:outline-none',
      },
    },

    defaultVariants: {
      size: 'medium',
      color: 'default',
    },
  },
);

function InputGroup({
  className,
  size,
  color,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupVariants>) {
  return (
    <div
      data-slot="input-group"
      className={cn(
        inputGroupVariants({ size, color }),
        // Variants based on alignment.
        'has-[>[data-align=inline-start]]:[&>input]:pl-2',
        'has-[>[data-align=inline-end]]:[&>input]:pr-2',
        'has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-start]]:[&>input]:pb-3',
        'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3',
        'has-[>[data-slot=popover-trigger]]:cursor-pointer',
        //Read only state.
        'has-[>[data-slot=input-group-control]:read-only]:pointer-events-none has-[>[data-slot=input-group-control]:read-only]:border has-[>[data-slot=input-group-control]:read-only]:border-border-primary has-[>[data-slot=input-group-control]:read-only]:bg-ui-field01 has-[>[data-slot=input-group-control]:read-only]:fill-text-secondary has-[>[data-slot=input-group-control]:read-only]:text-text-secondary',
        //Disabled state.
        'has-[>[data-slot=input-group-control]:disabled]:pointer-events-none has-[>[data-slot=input-group-control]:disabled]:bg-action-disabledBackground has-[>[data-slot=popover-trigger]:disabled]:pointer-events-none has-[>[data-slot=popover-trigger]:disabled]:border has-[>[data-slot=popover-trigger]:disabled]:border-border-primary has-[>[data-slot=popover-trigger]:disabled]:bg-action-disabledBackground has-[>[data-slot=popover-trigger]:disabled]:outline-none',
        className,
      )}
      {...props}
    />
  );
}
const inputGroupAddonVariants = cva(
  "flex h-fit cursor-pointer fill-icon-secondary select-none group-has-[>[data-slot=input-group-control]:disabled]/input-group:fill-action-disabled01 group-has-[>[data-slot=popover-trigger]:disabled]/input-group:fill-action-disabled01 focus-visible:outline-none [&>svg:not([class*='size-'])]:size-4",
  {
    variants: {
      align: {
        'inline-start': 'order-first pl-3',
        'inline-end': 'order-last pr-3',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  },
);

function InputGroupAddon({
  className,
  align = 'inline-start',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <span
      data-slot="input-group-addon"
      data-align={align}
      tabIndex={-1}
      className={cn(inputGroupAddonVariants({ align }), className)}
      {...props}
    >
      {props.children}
    </span>
  );
}

function InputGroupText({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="input-group-text"
      className={cn(
        'group-has-[>[data-slot=input-group-control]:disabled]/input-group:text-text-disabled02',
        typographyVariants({ variant: 'caption2', color: 'secondary' }),

        className,
      )}
      {...props}
    />
  );
}

function InputGroupInput({
  className,
  ...props
}: Readonly<React.ComponentProps<typeof Input>>) {
  return (
    <Input
      data-slot="input-group-control"
      className={cn(
        'border-none shadow-none outline-none focus-visible:outline-none disabled:bg-transparent-main data-[slot=popover-trigger]:cursor-pointer',
        className,
      )}
      {...props}
    />
  );
}

export { InputGroup, InputGroupAddon, InputGroupText, InputGroupInput };
